import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Descriptions, Empty, Row, Segmented, Space, Statistic, Table, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { api } from '../lib/api';

const { Title, Text } = Typography;

interface CountRow { name: string; count: number }
interface TerrainAlert { id: string; key: string; message: string; startedAt: string; endedAt: string | null }
interface TerrainOverview {
  enabled: boolean;
  days: number;
  snapshot: {
    checkedAt: string;
    health: boolean;
    ready: boolean | null;
    jobs: { queued: number; running: number; workers: number; capacity: number; retained: number } | null;
    dataset: { id: string; revision: string; coverageDescription: string | null } | null;
    disk: { path: string; freeBytes: number; totalBytes: number } | null;
    memory: { totalBytes: number; availableBytes: number; swapTotalBytes: number; swapFreeBytes: number };
  } | null;
  thresholds: { diskFreeGb: number; memoryFreeMb: number; failures: number };
  alerts: { active: TerrainAlert[]; recent: TerrainAlert[] };
  usage: {
    totals: { assessments: number; succeeded: number; failed: number; cancelled: number; expired: number; unfinished: number };
    successRate: number | null;
    durationMs: { median: number | null; p90: number | null; max: number | null };
    samplesEvaluated: { median: number | null; max: number | null };
    byDay: { day: string; assessments: number; failed: number }[];
    byApp: CountRow[];
    byOrganization: CountRow[];
    byUser: { userId: string; email: string; name: string | null; assessments: number; failed: number }[];
    failuresByCode: CountRow[];
    rejectionsByCode: CountRow[];
  };
}

const gb = (bytes: number) => `${(bytes / 1024 ** 3).toFixed(1)} GB`;
const seconds = (ms: number | null) => (ms === null ? '–' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);
const when = (value: string) => new Date(value).toLocaleString();

const countColumns = (title: string) => [
  { title, dataIndex: 'name', key: 'name' },
  { title: 'Count', dataIndex: 'count', key: 'count', align: 'right' as const },
];

function CountTable({ title, rows, label }: { title: string; rows: CountRow[]; label: string }) {
  return (
    <Card title={title} size="small">
      <Table size="small" pagination={false} rowKey="name" dataSource={rows} columns={countColumns(label)}
        locale={{ emptyText: 'None' }} />
    </Card>
  );
}

/** Terrain service status, alerts and usage (super-admins). */
export default function AdminTerrainPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<TerrainOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await api.admin.getTerrainOverview(days));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load terrain monitoring');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const timer = setInterval(() => { void load(); }, 60000);
    return () => clearInterval(timer);
  }, [load]);

  const snapshot = data?.snapshot;
  const usage = data?.usage;
  const memory = snapshot?.memory;
  const swapUsed = memory ? memory.swapTotalBytes - memory.swapFreeBytes : 0;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <Title level={3} style={{ margin: 0 }}>Terrain service</Title>
        <Space>
          <Segmented value={days} onChange={(value) => setDays(Number(value))}
            options={[{ label: '7 days', value: 7 }, { label: '30 days', value: 30 }, { label: '90 days', value: 90 }]} />
          <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>Refresh</Button>
        </Space>
      </Space>

      {error && <Alert type="error" message={error} showIcon />}
      {data && !data.enabled && (
        <Alert type="info" showIcon message="The terrain gateway is not configured on this server (TERRAIN_UPSTREAM)." />
      )}

      {data?.alerts.active.map((alert) => (
        <Alert key={alert.id} type="error" showIcon message={alert.message}
          description={`Alert ${alert.key} since ${when(alert.startedAt)}`} />
      ))}

      {data?.enabled && (
        <Card title="Status" size="small" extra={snapshot && <Text type="secondary">checked {when(snapshot.checkedAt)}</Text>}>
          {!snapshot ? <Empty description="No check yet; the first runs within a minute of APO starting." /> : (
            <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
              <Descriptions.Item label="Service">
                <Badge status={snapshot.health ? 'success' : 'error'} text={snapshot.health ? 'answering' : 'not answering'} />
              </Descriptions.Item>
              <Descriptions.Item label="Ready">
                {snapshot.ready === null ? '–' : snapshot.ready ? 'yes' : 'no (until the first assessment after a restart)'}
              </Descriptions.Item>
              <Descriptions.Item label="Jobs">
                {snapshot.jobs ? `${snapshot.jobs.running} running, ${snapshot.jobs.queued} queued (${snapshot.jobs.workers} worker(s), capacity ${snapshot.jobs.capacity})` : '–'}
              </Descriptions.Item>
              <Descriptions.Item label="Dataset">
                {snapshot.dataset ? <>{snapshot.dataset.id} <Text type="secondary" copyable>{snapshot.dataset.revision}</Text></> : '–'}
              </Descriptions.Item>
              <Descriptions.Item label="Coverage" span={2}>{snapshot.dataset?.coverageDescription || '–'}</Descriptions.Item>
              <Descriptions.Item label="Disk (elevation data)">
                {snapshot.disk ? `${gb(snapshot.disk.freeBytes)} free of ${gb(snapshot.disk.totalBytes)}` : 'unknown'}
                <Text type="secondary"> (alert below {data.thresholds.diskFreeGb} GB)</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Memory">
                {memory && `${gb(memory.availableBytes)} available of ${gb(memory.totalBytes)}, swap ${gb(swapUsed)} of ${gb(memory.swapTotalBytes)} used`}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Card>
      )}

      {usage && (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}><Card size="small"><Statistic title="Assessments" value={usage.totals.assessments} /></Card></Col>
            <Col xs={12} md={6}><Card size="small"><Statistic title="Success rate" value={usage.successRate === null ? '–' : `${Math.round(usage.successRate * 100)} %`} /></Card></Col>
            <Col xs={12} md={6}><Card size="small"><Statistic title="Median duration" value={seconds(usage.durationMs.median)} /></Card></Col>
            <Col xs={12} md={6}><Card size="small"><Statistic title="Slowest" value={seconds(usage.durationMs.max)} /></Card></Col>
          </Row>
          <Text type="secondary">
            {usage.totals.succeeded} succeeded, {usage.totals.failed} failed, {usage.totals.cancelled} cancelled, {usage.totals.expired} not followed to the end
            {usage.totals.unfinished ? `, ${usage.totals.unfinished} running` : ''}; 90th percentile duration {seconds(usage.durationMs.p90)};
            largest area {usage.samplesEvaluated.max?.toLocaleString() ?? '–'} elevation samples.
          </Text>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Per day" size="small">
                <Table size="small" pagination={{ pageSize: 10 }} rowKey="day" dataSource={[...usage.byDay].reverse()} locale={{ emptyText: 'None' }}
                  columns={[
                    { title: 'Day', dataIndex: 'day', key: 'day' },
                    { title: 'Assessments', dataIndex: 'assessments', key: 'assessments', align: 'right' },
                    { title: 'Failed', dataIndex: 'failed', key: 'failed', align: 'right' },
                  ]} />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Top users" size="small">
                <Table size="small" pagination={false} rowKey="userId" dataSource={usage.byUser} locale={{ emptyText: 'None' }}
                  columns={[
                    { title: 'User', key: 'user', render: (_, row) => row.name ? `${row.name} (${row.email})` : row.email },
                    { title: 'Assessments', dataIndex: 'assessments', key: 'assessments', align: 'right' },
                    { title: 'Failed', dataIndex: 'failed', key: 'failed', align: 'right' },
                  ]} />
              </Card>
            </Col>
            <Col xs={24} md={12} lg={6}><CountTable title="Per app" rows={usage.byApp} label="App" /></Col>
            <Col xs={24} md={12} lg={6}><CountTable title="Per organization" rows={usage.byOrganization} label="Organization" /></Col>
            <Col xs={24} md={12} lg={6}><CountTable title="Failures by code" rows={usage.failuresByCode} label="Code" /></Col>
            <Col xs={24} md={12} lg={6}><CountTable title="Refusals by code" rows={usage.rejectionsByCode} label="Code" /></Col>
          </Row>
        </>
      )}

      {data && (
        <Card title="Alert history" size="small">
          <Table size="small" pagination={{ pageSize: 10 }} rowKey="id" dataSource={data.alerts.recent} locale={{ emptyText: 'No alerts in this period' }}
            columns={[
              { title: 'Started', dataIndex: 'startedAt', key: 'startedAt', render: when },
              { title: 'Alert', dataIndex: 'key', key: 'key', render: (key: string) => <Tag>{key}</Tag> },
              { title: 'Message', dataIndex: 'message', key: 'message' },
              { title: 'Ended', dataIndex: 'endedAt', key: 'endedAt', render: (value: string | null) => (value ? when(value) : <Tag color="red">active</Tag>) },
            ]} />
        </Card>
      )}
    </Space>
  );
}
